/**
 * Error type & interfaces
 *
 * Why: Type safety and consistency across all error handling
 * What: Comprehensive type definations for error structures
 *
 * Benefits:
 *  - Typescript caches errors at compile time
 *  - Self-documenting code
 *  - Consistent error structure
 *
 * Design Decision: Using enums for category/severity to ensure
 * only valid values are used, making switch statments exhaustive
 */
/**
 * Error severity levels
 *
 * Used for:
 *  - Determining long levels
 *  - Priortizing error reporting
 *  - Deciding on user notification urgency
 *
 * Low: User can continue,minor minor inconvenience
 * Medium: Featur degraded but app works
 * High: Major feature broken
 * Critical: App unusable, needs immediate attention
 */

export const ErrorSeverity = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

export type ErrorSeverity = (typeof ErrorSeverity)[keyof typeof ErrorSeverity];
/**
 * Error categories
 *
 * Used for:
 *  - Routing to appropriate handler
 *  - Determining retry behaviour
 *  - Showing appropriate UI
 *
 * Categories are mutually exclusive - each error has exactly one
 */

export const ErrorCategory = {
  NETWORK: "network",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  VALIDATION: "validation",
  NOT_FOUND: "not_found",
  RATE_LIMIT: "rate_limit",
  /** Server error 5xx */
  SERVER: "server",
  /** Client error (4xx) */
  CLIENT: "client",
  /** Request Timeout */
  TIMEOUT: "timeout",
  /** Request was cancelled */
  CANCELLED: "cancelled",
  /** Unknown/unexpected error */
  UNKNOWN: "unknown",
} as const;

export type ErrorCategory = (typeof ErrorCategory)[keyof typeof ErrorCategory];

/**
 * Field-level
 * Used for mapping API validation errors to form fields
 * Compatible with most form libraries (react-hook)
 */
export interface ValidationFieldError {
  /** Form field name (dot notation for nested: "address.street") */
  field: string;
  /** Human readable error message */
  message: string;
  /** The invalid value (optional for debugging) */
  value?: unknown;
  /** Validation rule that failed (optional) */
  rule?: string;
}

/**
 * Error context
 *
 * Additional information about where/how error occured
 * Invaluable for debugging production issues
 */
export interface ErrorContext {
  /** Component or module where error occured */
  component?: string;
  /** Action being performed when error occured */
  action?: string;
  // User Id if authenticated
  userId?: string;
  /** Request Id for tracing through backend */
  requestId?: string;
  /** API endpoint that failed */
  url?: string;
  //** Http Method */
  method?: string;
  /** When error occured */
  timestamp: Date;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  path?: string;
  details?: Record<string, unknown>;
}

/**
 * Serialized error for logging/reporting
 *
 * This is what gets sent to error monitoring services
 * and written to logs.Contains all information needed
 * to debug the error without the error object itself.
 */

export interface SerializedError {
  /** Error class name */
  name: string;
  /** Error message */
  message: string;
  /**Http status code (0 for non-HTTP errors) */
  statusCode: number;
  /** Application error code */
  code: string;
  /** Error category */
  category: ErrorCategory;
  /** Error severity */
  severity: ErrorSeverity;
  /** Whether error can be retries */
  isRetryable: boolean;
  /** Whether error is operational (expected) vs programmer error */
  isOperational: boolean;
  /** Error Context */
  context?: ErrorContext;
  /** Validation field errors */
  validationErrors?: ValidationFieldError[];
  stack?: string;
  timestamp: string;
}

/**
 * Options for error handler
 *
 * Allows customizing error handling behavior per-call
 */
export interface ErrorHandlerOptions {
  // Show toast notification
  showToast?: boolean;
  /** Log error to console */
  logError?: boolean;
  /** Report error to monitoring service */
  reportError?: boolean;
  /** Rethrow error after handling */
  rethrow?: boolean;
  /** Custom fallback message if error message is technical */
  fallbackMessage?: string;
  /** Additional contet to add to error */
  context?: Partial<ErrorContext>;
}

/**
 * Retry configuration
 *
 * Used by HTTP service and React Query for retry behavior
 */
export interface RetryConfig {
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Base delay between reties ms */
  baseDelay: number;
  /** Maximum delay between retries ms */
  maxDelay: number;
  /** Custom condition for wether to retry */
  shouldRetry?: (error: unknown, attemptNumber: number) => boolean;
  /** Callback when retry is attempted */
  onRetry?: (attemptNumber: number, error: unknown, delay: number) => void;
}
